import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PlusCircle, KeyRound, Trash2, Users2, Building2, IndianRupee, Save } from 'lucide-react';

function NewUserDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', password: '', role: 'staff', assigned_company_ids: [], monthly_salary: '', working_days_per_month: 26 });
  const toggle = (cid) => setF((old) => ({ ...old, assigned_company_ids: old.assigned_company_ids.includes(cid) ? old.assigned_company_ids.filter((x) => x !== cid) : [...old.assigned_company_ids, cid] }));
  const submit = async () => {
    if (!f.name || !f.email || !f.password) return toast.error('Fill all required fields');
    try {
      await api.post('/admin/users', {
        ...f,
        monthly_salary: Number(f.monthly_salary || 0),
        working_days_per_month: Number(f.working_days_per_month || 26),
      });
      toast.success('User created');
      setOpen(false);
      setF({ name: '', email: '', password: '', role: 'staff', assigned_company_ids: [], monthly_salary: '', working_days_per_month: 26 });
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const needsAssignment = f.role !== 'admin';
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-add-user-button"><PlusCircle className="h-4 w-4 mr-2" />Add user</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create HR user</DialogTitle>
          <DialogDescription>Admins can create Managers and Staff, and assign them to specific companies. Salary & working days feed the recruitment cost analytics.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Temporary password *</Label><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">HR Manager</SelectItem>
                  <SelectItem value="staff">HR Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Monthly salary (INR)</Label>
              <Input type="number" min="0" value={f.monthly_salary} onChange={(e) => setF({ ...f, monthly_salary: e.target.value })} data-testid="new-user-salary" placeholder="e.g., 50000" />
            </div>
            <div className="space-y-1.5">
              <Label>Working days / month</Label>
              <Input type="number" min="1" max="31" value={f.working_days_per_month} onChange={(e) => setF({ ...f, working_days_per_month: e.target.value })} data-testid="new-user-working-days" />
            </div>
          </div>

          {needsAssignment && (
            <div className="space-y-1.5">
              <Label>Assigned companies</Label>
              <div className="max-h-44 overflow-y-auto rounded-lg border p-2 space-y-1" data-testid="new-user-companies">
                {companies.length === 0 && <p className="text-xs text-muted-foreground p-2">No companies exist yet.</p>}
                {companies.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-2 py-1">
                    <input type="checkbox" checked={f.assigned_company_ids.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`new-user-company-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">This user will only see data for the selected companies.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="new-user-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignCompaniesDialog({ user, companies, onSaved }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(user.assigned_company_ids || []);
  useEffect(() => { if (open) setIds(user.assigned_company_ids || []); }, [open, user]);

  const toggle = (cid) => setIds((old) => (old.includes(cid) ? old.filter((x) => x !== cid) : [...old, cid]));
  const save = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, { assigned_company_ids: ids });
      toast.success('Assignments updated');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`assign-companies-${user.id}`}><Building2 className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign companies to {user.name}</DialogTitle>
          <DialogDescription>{user.name} will only see workspaces for the companies selected below.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-lg border p-2 space-y-1">
          {companies.length === 0 && <p className="text-xs text-muted-foreground p-2">No companies exist yet.</p>}
          {companies.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-2 py-1">
              <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`assign-${user.id}-${c.id}`} />
              {c.name}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid={`assign-save-${user.id}`}><Save className="h-4 w-4 mr-2" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalaryDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [salary, setSalary] = useState(user.monthly_salary || 0);
  const [wdays, setWdays] = useState(user.working_days_per_month || 26);
  useEffect(() => { if (open) { setSalary(user.monthly_salary || 0); setWdays(user.working_days_per_month || 26); } }, [open, user]);

  const save = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, { monthly_salary: Number(salary), working_days_per_month: Number(wdays) });
      toast.success('Cost inputs updated');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`salary-btn-${user.id}`}><IndianRupee className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cost inputs · {user.name}</DialogTitle>
          <DialogDescription>Used only by the Recruitment Cost analytics. Not shown to the user themselves.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 grid-cols-2">
          <div className="space-y-1.5">
            <Label>Monthly salary (INR)</Label>
            <Input type="number" min="0" value={salary} onChange={(e) => setSalary(e.target.value)} data-testid={`salary-input-${user.id}`} />
          </div>
          <div className="space-y-1.5">
            <Label>Working days / month</Label>
            <Input type="number" min="1" max="31" value={wdays} onChange={(e) => setWdays(e.target.value)} data-testid={`working-days-input-${user.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid={`salary-save-${user.id}`}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pw, setPw] = useState({ id: null, val: '' });

  const load = async () => {
    const [u, c] = await Promise.all([api.get('/admin/users'), api.get('/companies')]);
    setUsers(u.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const companiesById = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);

  const toggleActive = async (u) => { const path = u.active ? 'revoke' : 'activate'; await api.post(`/admin/users/${u.id}/${path}`); toast.success('Updated'); load(); };
  const del = async (id) => { await api.delete(`/admin/users/${id}`); toast.success('Deleted'); load(); };
  const patchUser = async (id, patch) => { await api.patch(`/admin/users/${id}`, patch); load(); };
  const changePw = async () => {
    if (!pw.val || pw.val.length < 6) return toast.error('Password must be 6+ chars');
    await api.post(`/admin/users/${pw.id}/password`, { new_password: pw.val });
    toast.success('Password reset'); setPw({ id: null, val: '' });
  };

  return (
    <>
      <PageHeader
        title="User Management"
        description="Create HR users, assign companies, set cost inputs, and revoke or reset access."
        actions={<NewUserDialog companies={companies} onCreated={load} />}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {users.length === 0 ? (
          <EmptyState icon={Users2} title="No users yet" description="Create the first HR user to expand your team." />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Assigned companies</TableHead>
                  <TableHead className="text-right">Salary / working days</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <TableCell>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => patchUser(u.id, { role: v })}>
                        <SelectTrigger className="h-8 w-32" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {u.role === 'admin' ? (
                        <Badge variant="outline">All companies</Badge>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1 max-w-xs">
                          {(u.assigned_company_ids || []).slice(0, 3).map((cid) => (
                            <Badge key={cid} variant="secondary" className="text-[11px]">{companiesById[cid]?.name || cid.slice(0, 8)}</Badge>
                          ))}
                          {(u.assigned_company_ids || []).length > 3 && (
                            <Badge variant="secondary" className="text-[11px]">+{u.assigned_company_ids.length - 3}</Badge>
                          )}
                          {(!u.assigned_company_ids || u.assigned_company_ids.length === 0) && (
                            <span className="text-[11px] text-muted-foreground">None</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <p className="text-sm">₹ {Number(u.monthly_salary || 0).toLocaleString('en-IN')}</p>
                      <p className="text-[11px] text-muted-foreground">{u.working_days_per_month || 26} days/mo</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`active-switch-${u.id}`} />
                        <span className="text-xs text-muted-foreground">{u.active ? 'active' : 'revoked'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {u.role !== 'admin' && (
                          <AssignCompaniesDialog user={u} companies={companies} onSaved={load} />
                        )}
                        <SalaryDialog user={u} onSaved={load} />
                        <Dialog open={pw.id === u.id} onOpenChange={(o) => setPw({ id: o ? u.id : null, val: '' })}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`change-pw-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reset password for {u.name}</DialogTitle>
                              <DialogDescription>The user must sign in with the new password. It replaces the existing password immediately.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-1.5"><Label>New password</Label><Input value={pw.val} onChange={(e) => setPw({ ...pw, val: e.target.value })} data-testid="new-password-input" /></div>
                            <DialogFooter><Button onClick={changePw} data-testid="confirm-change-pw">Reset</Button></DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This user will lose access immediately.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(u.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
}
